import React, { useRef, useState, useEffect } from "react";
import { Xmark, ArrowShapeUpFromLine, Eye, TriangleRight, TriangleRightFill, PauseFill, TrashBin, ArrowRotateRight, VolumeLow, VolumeOff, VolumeXmark, Volume} from "@gravity-ui/icons";
import { Loader } from "@gravity-ui/uikit";
import { usePostHog } from "posthog-js/react";
import Toast from "../generic_components/Toast";
import { useLocalState } from "../../contexts/LocalStateProvider";
import { useAudioPlayer } from "../../contexts/AudioPlayerContext";
import * as Sentry from "@sentry/react"
const PreviewPanel = ({
  className,
  onClose,
  isAnotherPanelOpen = false
}) => {
  const { localState } = useLocalState();
  const { stopAudio } = useAudioPlayer();
  const posthog = usePostHog();
  const dropRef = useRef(null);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState(null);
  const [fileTypes] = useState([".mp4", ".mov", ".avi", ".wmv", ".webm"]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [musicContentFit, setMusicContentFit] = useState(80);
  const [videoVolume, setVideoVolume] = useState(0);
  const [mediaVolume, setMediaVolume] = useState(50);
  const videoRef = useRef(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const videoVolumeRef = useRef(null);
  const mediaVolumeRef = useRef(null);
  const [isDraggingVideoVolume, setIsDraggingVideoVolume] = useState(false);
  const [isDraggingMediaVolume, setIsDraggingMediaVolume] = useState(false);
  const [isProgressHovered, setIsProgressHovered] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const fileInputRef = useRef(null);
  const [isAudioLoaded, setIsAudioLoaded] = useState(false);

  // State for the draggable generated audio slider
  const [generatedAudioDuration, setGeneratedAudioDuration] = useState(null);
  const [generatedAudioCover, setGeneratedAudioCover] = useState(null);
  const [generatedAudioName, setGeneratedAudioName] = useState(null);
  const [generatedAudioSrc, setGeneratedAudioSrc] = useState(null);
  const [audioStartTime, setAudioStartTime] = useState(0);
  const [isDraggingAudio, setIsDraggingAudio] = useState(false);
  const audioSliderRef = useRef(null);
  const progressBarContainerRef = useRef(null);
  const dragStartXRef = useRef(0);
  const dragStartAudioTimeRef = useRef(0);
  const generatedAudioRef = useRef(null);

  // State for Music Content Fit Analysis
  const [isUploading, setIsUploading] = useState(false); // Tracks GCS upload status
  const [isAnalyzing, setIsAnalyzing] = useState(false); // Tracks CF analysis status
  const [fitScore, setFitScore] = useState(null); // Stores the numerical score (0-100)
  const [justification, setJustification] = useState(''); // Stores the explanation string
  const [gcsUploadUri, setGcsUploadUri] = useState(null); // Stores the gs:// path after upload
  const [analysisError, setAnalysisError] = useState(null); // Stores any error during analysis
  const [musicCaptionForAnalysis, setMusicCaptionForAnalysis] = useState(''); // Stores music description for analysis

  // Get generated audio details from localState
  useEffect(() => {
    if (localState.currentSongDetails) {
      console.log("Received song details in PreviewPanel:", localState.currentSongDetails);
      const details = localState.currentSongDetails;
      
      // Duration
      const audioDuration = details.duration;
      if (audioDuration) {
        let durationInSeconds;
        if (typeof audioDuration === 'string' && audioDuration.includes(':')) {
            const parts = audioDuration.split(':');
            durationInSeconds = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
        } else {
            durationInSeconds = parseFloat(audioDuration);
        }
        if (!isNaN(durationInSeconds) && durationInSeconds > 0) {
            setGeneratedAudioDuration(durationInSeconds);
        } else {
            setGeneratedAudioDuration(null);
        }
      } else {
        setGeneratedAudioDuration(null);
      }

      // Cover Image
      setGeneratedAudioCover(details.coverImage || null);
      // Name
      setGeneratedAudioName(details.name || null);
      // Audio Source URL
      const src = details.audioSrc || null;
      setGeneratedAudioSrc(src);
      console.log("Setting generatedAudioSrc:", src); // Log the audio source
      
      // Extract music description for analysis
      const musicDescription = details.description || ''; // Get the description
      setMusicCaptionForAnalysis(musicDescription); // Store it in state
      
      setAudioStartTime(0);
    } else {
        console.log("No currentSongDetails found in localState for PreviewPanel.");
        setGeneratedAudioDuration(null);
        setGeneratedAudioCover(null);
        setGeneratedAudioName(null);
        setGeneratedAudioSrc(null);
        setMusicCaptionForAnalysis(''); // Clear description state
    }
  }, [localState.currentSongDetails]);

  // Add a new effect to preload and prepare the audio element
  useEffect(() => {
    if (generatedAudioSrc && generatedAudioRef.current) {
      console.log("Preloading audio file:", generatedAudioSrc);
      // Reset audio loaded state when source changes
      setIsAudioLoaded(false);
      // Set the source and preload it
      generatedAudioRef.current.src = generatedAudioSrc;
      generatedAudioRef.current.load();
      // Set initial volume
      generatedAudioRef.current.volume = mediaVolume / 100;
    } else if (!generatedAudioSrc) {
      // If there's no audio source, mark as "loaded" to allow video playback without audio
      setIsAudioLoaded(true);
    }
  }, [generatedAudioSrc]); // Remove mediaVolume from dependencies

  // Separate effect for handling volume changes
  useEffect(() => {
    if (generatedAudioRef.current) {
      generatedAudioRef.current.volume = mediaVolume / 100;
    }
  }, [mediaVolume]);

  // Effect to close this panel and stop audio when another panel opens
  useEffect(() => {
    if (isAnotherPanelOpen) {
      if (videoRef.current && isPlaying) {
        videoRef.current.pause();
      }
      if (generatedAudioRef.current && !generatedAudioRef.current.paused) {
          generatedAudioRef.current.pause();
      }
      setIsPlaying(false);
      onClose();
    }
  }, [isAnotherPanelOpen, onClose, isPlaying]);

  // Effect to close this panel and stop audio when details panel opens
  useEffect(() => {
    if (localState.isDetailsPanelOpen) {
      if (videoRef.current && isPlaying) {
        videoRef.current.pause();
      }
      if (generatedAudioRef.current && !generatedAudioRef.current.paused) {
          generatedAudioRef.current.pause();
      }
      setIsPlaying(false);
      onClose();
    }
  }, [localState.isDetailsPanelOpen, onClose, isPlaying]);

  // Effect for video event listeners (timeupdate, duration, ended, error)
  useEffect(() => {
    const videoElement = videoRef.current;
    const audioElement = generatedAudioRef.current;

    if (videoElement && audioUrl) { 
      const updateTime = () => {
        const videoTime = videoElement.currentTime;
        setCurrentTime(videoTime);

        // If video is playing but we should be waiting for audio to load, pause it
        if (!isAudioLoaded && generatedAudioSrc && isPlaying && !videoElement.paused) {
          console.log("Pausing video because audio is not loaded yet");
          videoElement.pause();
          setToastMessage("Waiting for audio to load before playing");
          setToastType("warning"); // Set the toast type to warning
          // Don't set isPlaying to false to maintain the intent to play once loaded
          return;
        }

        // --- Refined Audio Sync Logic within timeupdate --- 
        if (canShowAudioSlider && audioElement) {
          const audioShouldBeInRange = videoTime >= audioStartTime && videoTime < (audioStartTime + generatedAudioDuration);
          const audioIsCurrentlyPlaying = !audioElement.paused;

          if (isPlaying) { // Only manage audio play/pause if video is supposed to be playing
              if (audioShouldBeInRange && !audioIsCurrentlyPlaying) {
                  // Video playing, in range, audio paused -> Play audio
                  const audioTimeToSet = Math.max(0, videoTime - audioStartTime);
                  if (isFinite(audioTimeToSet) && audioTimeToSet < generatedAudioDuration) {
                      console.log(`TimeUpdate: Syncing audio to ${audioTimeToSet} and playing.`);
                      if (Math.abs(audioElement.currentTime - audioTimeToSet) > 0.1) {
                         audioElement.currentTime = audioTimeToSet;
                      }
                      // **** ADD CHECK HERE ****
                      // Only play if the video element itself is not paused
                      if (!videoElement.paused) { 
                        audioElement.play().catch(e => console.error("Audio play error on timeupdate sync:", e), Sentry.captureException(e));
                      }
                  }
              } else if (!audioShouldBeInRange && audioIsCurrentlyPlaying) {
                  // Video playing, out of range, audio playing -> Pause audio
                  audioElement.pause();
                  console.log(`TimeUpdate: Video at ${videoTime}, outside audio range [${audioStartTime}, ${audioStartTime + generatedAudioDuration}], pausing audio.`);
              }
          } else { // If video is paused
              // Ensure audio is also paused if video is not playing
              if (audioIsCurrentlyPlaying) {
                  audioElement.pause();
                  console.log("TimeUpdate: Video is paused, ensuring audio is paused.");
              }
          }
        }
        // --- End Audio Sync Logic ---
      };

      const updateDuration = () => {
        if (videoElement.duration && isFinite(videoElement.duration)) {
            setDuration(videoElement.duration);
        } else {
            console.log("Invalid video duration detected:", videoElement.duration);
        }
      };

      const handleVideoEnd = () => {
        setIsPlaying(false);
        if (audioElement) {
            audioElement.pause(); // Ensure audio stops when video ends
        }
      };

      const handleVideoError = (e) => {
          console.error("Video error:", e);
          setToastMessage("Error playing video file");
          if (audioElement) audioElement.pause(); // Pause audio on video error
      };

      videoElement.addEventListener("timeupdate", updateTime);
      videoElement.addEventListener("loadedmetadata", updateDuration);
      videoElement.addEventListener("durationchange", updateDuration);
      videoElement.addEventListener("canplay", updateDuration);
      videoElement.addEventListener("ended", handleVideoEnd);
      videoElement.addEventListener("error", handleVideoError);
      
      updateDuration();

      return () => {
        videoElement.removeEventListener("timeupdate", updateTime);
        videoElement.removeEventListener("loadedmetadata", updateDuration);
        videoElement.removeEventListener("durationchange", updateDuration);
        videoElement.removeEventListener("canplay", updateDuration);
        videoElement.removeEventListener("ended", handleVideoEnd);
        videoElement.removeEventListener("error", handleVideoError);
      };
    }
  }, [audioUrl, canShowAudioSlider, isPlaying, audioStartTime, generatedAudioDuration, isAudioLoaded]);

  // Effect for setting video volume/muted
  useEffect(() => {
    if (videoRef.current) {
      const isMuted = videoVolume === 0;
      videoRef.current.volume = videoVolume / 100;
      videoRef.current.muted = isMuted;
    }
  }, [videoVolume]);

  // Effect to stop main audio player when component mounts
  useEffect(() => {
    stopAudio();
  }, [stopAudio]); // Include stopAudio in dependency array

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOptionsModal && !event.target.closest('.options-modal') && !event.target.closest('.options-button')) {
        setShowOptionsModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptionsModal]);

  // --- Update File Handling for GCS Upload --- 
  const handleFileChange = async (e) => { // Make async
    posthog.capture("Click-UploadVideo-PreviewPanel", {
      is_user_logged_in: true
    })
    const file = e.target.files[0];
    if (!file) {
      console.log("No file selected in handleFileChange");
      return;
    }

    console.log("Starting file change process with file:", file.name);

    // Reset previous analysis/upload state but preserve music info
    setFitScore(null);
    setJustification('');
    setAnalysisError(null);
    setIsUploading(true); // Show uploading state
    setIsAnalyzing(false);
    setGcsUploadUri(null);
    
    if (file.size > 20 * 1024 * 1024) {
      setToastMessage("File size exceeds 20MB limit");
      setIsUploading(false);
      return;
    }
    
    if (!file.type.startsWith("video/")) { 
      setToastMessage("Please upload a video file");
      setIsUploading(false);
      return;
    }
    
    // Clear previous file now that we've validated the new one
    setAudioUrl(null);
    setUploadedFile(null);
    
    // Set file for local preview first (using Blob URL)
    console.log("File selected for preview:", file);
    setUploadedFile(file); 
    setCurrentTime(0);
    setIsPlaying(false);
    
    // Reset audio start time for the new video
    setAudioStartTime(0);

    // Start GCS Upload process using the new Cloud Function
    setAnalysisError(null); // Clear previous errors
    console.log("Initiating GCS Upload via uploadContent Cloud Function for:", file.name);
    posthog.capture("GCS Upload Initiated via CF", { is_user_logged_in: true, fileType: file.type, fileSize: file.size });

    let gcsUri = null; // Initialize gcsUri

    try {
      // Create FormData to send the file
      const formData = new FormData();
      formData.append('videoFile', file, file.name); // Key must match CF expectation

      // Define the Cloud Function endpoint
      const uploadFunctionUrl = 'https://us-central1-wubble2024.cloudfunctions.net/uploadContent';

      console.log(`Uploading ${file.name} to ${uploadFunctionUrl}`);
      
      // Make the POST request to the Cloud Function
      const uploadResponse = await fetch(uploadFunctionUrl, {
        method: 'POST',
        body: formData, 
        // No Content-Type header needed for FormData, browser sets it with boundary
      });

      if (!uploadResponse.ok) {
        let errorMsg = `Upload function failed with status ${uploadResponse.status}`;
        try {
          const errorBody = await uploadResponse.json();
          errorMsg = errorBody.error || errorMsg;
          console.error("Upload function error response:", errorBody);
        } catch (jsonError) {
          console.error("Could not parse error response as JSON");
          Sentry.captureException(jsonError);
        }
        throw new Error(errorMsg);
      }

      // Get the GCS URI from the response
      const result = await uploadResponse.json();
      if (!result.gcsUri) {
        throw new Error("Cloud Function did not return a gcsUri.");
      }
      
      gcsUri = result.gcsUri;
      console.log("GCS Upload complete via CF. URI:", gcsUri);
      setGcsUploadUri(gcsUri);
      
    } catch (error) {
      console.error("Error during file upload via Cloud Function:", error);
      Sentry.captureException(error);
      setToastMessage("Upload failed: " + (error.message || "Unknown error"));
      setIsUploading(false);
      setIsAnalyzing(false);
      return; // Stop execution if upload fails
    } finally {
        // Set uploading to false regardless of success/failure *after* potential analysis call
        // Analysis happens after this block if successful
    }

    setIsUploading(false); // Mark upload as complete here, before analysis starts

    // 4. Call the analysis function if we have a music description and upload was successful
    if (gcsUri && musicCaptionForAnalysis && musicCaptionForAnalysis.trim() !== '') {
      try {
        console.log("Proceeding to analysis with URI:", gcsUri);
        await callAnalyzeFunction(gcsUri);
      } catch (analysisError) {
        console.error("Analysis failed:", analysisError);
        Sentry.captureException(analysisError);
        // UI will show the error in the fit section
      }
    } else if (!gcsUri) {
      console.log("Skipping analysis because GCS URI is not available after upload attempt.");
    } else {
      console.log("Skipping analysis because no music description is available");
      // Set a default message instead of an error
      setJustification("Upload a music track to see how it fits with this video.");
    }
  };

  // --- Helper Function to Call Analysis Cloud Function ---
  const callAnalyzeFunction = async (gcsUriToAnalyze) => {
    if (!musicCaptionForAnalysis || musicCaptionForAnalysis.trim() === '') {
        console.error("Cannot analyze: Music caption is missing.");
        setAnalysisError("Music description not available for analysis.");
        return;
    }
    if (!gcsUriToAnalyze) {
        console.error("Cannot analyze: GCS URI is missing.");
        setAnalysisError("Video GCS path not available.");
        return;
    }

    console.log(`Calling analysis function for ${gcsUriToAnalyze} with caption: "${musicCaptionForAnalysis}"`);
    setIsAnalyzing(true);
    setFitScore(null); // Clear previous results
    setJustification('');
    setAnalysisError(null);

    const functionUrl = 'https://us-central1-wubble2024.cloudfunctions.net/analyzeVideoMusicFit';
    
    // Sanitize the music caption to ensure it doesn't have weird characters
    const sanitizedCaption = musicCaptionForAnalysis.trim();
    
    const payload = {
        gcsUri: gcsUriToAnalyze,
        musicCaption: sanitizedCaption
    };
    
    // Log the object itself before stringifying
    console.log("Payload object before stringify:", payload); 
    // Log the JSON string being sent
    console.log("Sending analysis request with payload JSON string:", JSON.stringify(payload));

    try {
        const response = await fetch(functionUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        let data;
        const responseText = await response.text();
        try {
            data = JSON.parse(responseText);
        } catch (e) {
            console.error("Failed to parse response as JSON:", responseText);
            Sentry.captureException(e);
            throw new Error("Invalid JSON response from server");
        }

        if (!response.ok) {
            console.error("Analysis function error response:", data);
            // Create a more user-friendly error message
            let specificErrorMessage = `Request failed with status ${response.status}`; // Default
            if (data && data.error) {
                specificErrorMessage = data.error; // Use specific error from CF if available
            }

            if (response.status === 400) {
                setAnalysisError("The video format could not be analyzed. Please try a different video.");
            } else if (response.status === 500) {
                // Use the specific error from the response if available, otherwise generic
                setAnalysisError(`Analysis server error: ${specificErrorMessage}. Please try again later.`);
            } else {
                setAnalysisError(`Error: ${specificErrorMessage}`);
            }
            // Keep the placeholder justification
            setJustification("We couldn't analyze how well this music fits with your video.");
            return;
        }

        if (data.success) {
            console.log("Analysis successful:", data);
            setFitScore(data.score);
            setJustification(data.justification);
            // Optionally store transcript/summary if needed later
            // setTranscript(data.transcript);
            // setVideoSummary(data.video_summary);
        } else {
            throw new Error(data.error || 'Analysis function returned success=false');
        }

    } catch (error) {
        console.error("Error calling analysis function:", error);
        Sentry.captureException(error);
        setAnalysisError(error.message || "An unknown error occurred during analysis.");
        setFitScore(null); // Clear score on error
        setJustification("We couldn't analyze how well this music fits with your video.");
    } finally {
        setIsAnalyzing(false);
    }
  };

  // Update handleDrop to call the new async handleFileChange
  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];

    if (droppedFile) {
      // Create a mock event object to pass to handleFileChange
      const fileEvent = {
        target: { files: [droppedFile] },
      };
      handleFileChange(fileEvent); // Call the updated handler
    } 
    // Removed validation here as handleFileChange does it
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };
  
  const formatTime = (time) => {
    if (isNaN(time) || time === Infinity) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
  };
  
  const handlePlayPause = () => {
    posthog.capture("Click-Play/Pause Video-PreviewPanel", {
      is_user_logged_in: true
    })
    const videoElement = videoRef.current;
    const audioElement = generatedAudioRef.current;
    if (!videoElement) return;

    // If video is already playing, we always allow pausing
    if (isPlaying) {
      // --- Pause ---
      videoElement.pause();
      if (audioElement) {
          audioElement.pause(); // Always pause audio when video pauses explicitly
          console.log("Pause: Video and Audio paused.");
      }
      setIsPlaying(false);
      return;
    }
    
    // Check if we have audio that should be loaded first
    if (generatedAudioSrc && !isAudioLoaded) {
      console.log("Attempted to play before audio is loaded");
      setToastMessage("Please wait for audio to load before playing");
      setToastType("warning"); // Set the toast type to warning
      return;
    }

    // If we reach here, we're trying to start playback and audio is loaded or not required
    setIsPlaying(true);
    
    // --- Play ---
    videoElement.play().catch(e => {
      console.error("Video play error:", e);
      Sentry.captureException(e);
      setToastMessage("Could not play video");
      setIsPlaying(false); // Revert state on error
      if (audioElement) audioElement.pause(); // Ensure audio is paused on error
    });

    // Sync audio *after* starting video playback attempt
    if (audioElement && generatedAudioSrc && isAudioLoaded) {
        const videoTime = videoElement.currentTime;
        
        const audioShouldPlay = videoTime >= audioStartTime && videoTime < (audioStartTime + generatedAudioDuration);
        if (audioShouldPlay || !canShowAudioSlider) { // Allow playing even if slider calculation not ready
            const audioTimeToSet = Math.max(0, videoTime - audioStartTime);
            if (isFinite(audioTimeToSet) && (!generatedAudioDuration || audioTimeToSet < generatedAudioDuration)) {
              console.log(`Play: Syncing audio to ${audioTimeToSet} based on video time ${videoTime}`);
              audioElement.currentTime = audioTimeToSet;
              audioElement.volume = mediaVolume / 100; // Ensure volume is set
              audioElement.muted = false; // Explicitly unmute before playing
              
              // Always attempt to play audio if we have a source, regardless of slider visibility
              const playPromise = audioElement.play();
              if (playPromise !== undefined) {
                  playPromise.catch(e => {
                      console.error("Audio play error on main play sync:", e);
                      Sentry.captureException(e);
                      // Try playing one more time after a small delay
                      setTimeout(() => {
                          audioElement.play().catch(innerErr => 
                              console.error("Retry audio play failed:", innerErr));
                              Sentry.captureException(innerErr);
                      }, 100);
                  });
              }
            }
        } else {
            // If starting play outside the audio range, ensure audio is paused
            audioElement.pause();
            console.log(`Play: Video time ${videoTime} is outside audio range [${audioStartTime}, ${audioStartTime + generatedAudioDuration}]. Audio remains paused.`);
        }
    }
  };
  
  const handleSeek = (e) => {
    if (!videoRef.current || duration === 0 || isNaN(duration)) return;
    
    // If we're seeking during playback and audio isn't loaded, show warning
    if (isPlaying && generatedAudioSrc && !isAudioLoaded) {
      setToastMessage("Please wait for audio to load before seeking during playback");
      return;
    }
    
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const seekTime = (offsetX / progressBar.clientWidth) * duration;
    const audioElement = generatedAudioRef.current;
    
    if (!isNaN(seekTime) && isFinite(seekTime)) {
        // Update video
        videoRef.current.currentTime = seekTime;
        setCurrentTime(seekTime);

        // Update audio
        if (canShowAudioSlider && audioElement && isAudioLoaded) {
            audioElement.pause(); // Pause audio during seek
            const audioShouldPlay = seekTime >= audioStartTime && seekTime < (audioStartTime + generatedAudioDuration);
            if (audioShouldPlay) {
                const audioTimeToSet = Math.max(0, seekTime - audioStartTime);
                 if (isFinite(audioTimeToSet) && audioTimeToSet < generatedAudioDuration) {
                    audioElement.currentTime = audioTimeToSet;
                    // If the video *was* playing, resume audio playback after seek
                    if (isPlaying) { 
                         audioElement.play().catch(e => console.error("Audio play error on seek:", e), Sentry.captureException(e));
                    }
                 }
            }
        }
    }
  };
  
  const handleRewind = () => {
    posthog.capture("Click-GoBackward10s-PreviewPanel", {is_user_logged_in: true})
    if (videoRef.current) {
        const newTime = Math.max(0, videoRef.current.currentTime - 5);
        videoRef.current.currentTime = newTime;
        setCurrentTime(newTime);
        
        // Also seek the audio element if applicable
        const audioElement = generatedAudioRef.current;
        if (canShowAudioSlider && audioElement && isAudioLoaded) {
            audioElement.pause(); // Pause during seek adjustment
            const audioShouldPlay = newTime >= audioStartTime && newTime < (audioStartTime + generatedAudioDuration);
            if (audioShouldPlay) {
                const audioTimeToSet = Math.max(0, newTime - audioStartTime);
                 if (isFinite(audioTimeToSet) && audioTimeToSet < generatedAudioDuration) {
                    audioElement.currentTime = audioTimeToSet;
                    // If the video *was* playing, resume audio playback after seek
                    if (isPlaying) { 
                         audioElement.play().catch(e => console.error("Audio play error on rewind:", e), Sentry.captureException(e));
                    }
                 }
            }
        }
    }
  };
  
  const handleForward = () => {
    posthog.capture("Click-GoForward10s-PreviewPanel", {
      is_user_logged_in: true
    })
    if (videoRef.current && !isNaN(duration)) {
        const newTime = Math.min(duration, videoRef.current.currentTime + 5);
        videoRef.current.currentTime = newTime;
        setCurrentTime(newTime);
        
        // Also seek the audio element if applicable
        const audioElement = generatedAudioRef.current;
        if (canShowAudioSlider && audioElement && isAudioLoaded) {
            audioElement.pause(); // Pause during seek adjustment
            const audioShouldPlay = newTime >= audioStartTime && newTime < (audioStartTime + generatedAudioDuration);
            if (audioShouldPlay) {
                const audioTimeToSet = Math.max(0, newTime - audioStartTime);
                 if (isFinite(audioTimeToSet) && audioTimeToSet < generatedAudioDuration) {
                    audioElement.currentTime = audioTimeToSet;
                    // If the video *was* playing, resume audio playback after seek
                    if (isPlaying) { 
                         audioElement.play().catch(e => console.error("Audio play error on forward:", e), Sentry.captureException(e));
                    }
                 }
            }
        }
    }
  };

  const handleVideoVolumeChange = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.min(100, Math.max(0, (x / rect.width) * 100));
    setVideoVolume(percentage);
  };

  const handleMediaVolumeChange = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.min(100, Math.max(0, (x / rect.width) * 100));
    setMediaVolume(percentage);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDraggingVideoVolume && videoVolumeRef.current) {
        const rect = videoVolumeRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = Math.min(100, Math.max(0, (x / rect.width) * 100));
        setVideoVolume(percentage);
      }
      
      if (isDraggingMediaVolume && mediaVolumeRef.current) {
        const rect = mediaVolumeRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = Math.min(100, Math.max(0, (x / rect.width) * 100));
        setMediaVolume(percentage);
      }
    };
    
    const handleMouseUp = () => {
      setIsDraggingVideoVolume(false);
      setIsDraggingMediaVolume(false);
    };
    
    if (isDraggingVideoVolume || isDraggingMediaVolume) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingVideoVolume, isDraggingMediaVolume]);

  const toggleVideoMute = () => {
    if (videoVolume > 0) {
      setVideoVolume(0);
    } else {
      setVideoVolume(50);
    }
  };

  const toggleMediaMute = () => {
    if (mediaVolume > 0) {
      setMediaVolume(0);
    } else {
      setMediaVolume(50);
    }
  };

  const handleOptionsClick = () => {
    setShowOptionsModal(!showOptionsModal);
  };

  const handleReplace = () => {
    console.log("Replace clicked");
    setShowOptionsModal(false);
    posthog.capture("Click-Replace-PreviewPanel", {is_user_logged_in: true})
    
    if (fileInputRef.current) {
      fileInputRef.current.click();
      
      posthog.capture("Replace Audio File Initiated", { 
        is_user_logged_in: true,
        currentFileType: uploadedFile?.type,
        currentFileSize: uploadedFile?.size
      });
    } else {
      console.error("File input element not found");
      setToastMessage("Could not access file browser");
    }
  };

  const handleRemove = () => {
    posthog.capture("Click-Remove-PreviewPanel", {
      is_user_logged_in: true
    })
    if (videoRef.current && isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
    
    // Stop audio if playing
    if (generatedAudioRef.current && !generatedAudioRef.current.paused) {
      generatedAudioRef.current.pause();
    }
    
    setShowOptionsModal(false);
    setUploadedFile(null);
    setCurrentTime(0);
    setDuration(0);
    
    // DO NOT reset music-related states, only reset video-related states
    // We'll keep the audio information when changing videos
    setAudioStartTime(0); // Reset start time but keep other audio properties
    
    // Reset analysis states
    setFitScore(null);
    setJustification('');
    setAnalysisError(null);
    setGcsUploadUri(null);
    // Don't clear musicCaptionForAnalysis as we want to keep music info
    
    // Reset loading states
    setIsUploading(false);
    setIsAnalyzing(false);
    setIsLoading(false);
    
    // Reset file input element to ensure it can accept new files
    if (fileInputRef.current) {
      // Fully reset the file input value
      fileInputRef.current.value = "";
      console.log("File input value reset");
    }
    
    posthog.capture("Video File Removed", { 
      is_user_logged_in: true,
      fileType: uploadedFile?.type,
      fileSize: uploadedFile?.size
    });
    
    setToastMessage("Video removed successfully");
    
    // Only reset video volume, keep music volume intact
    setVideoVolume(0);
    
    // Make sure UI shows upload area
    setAudioUrl(null);
  };

  // --- Drag Handling for Audio Slider ---
  const handleAudioMouseDown = (e) => {
    posthog.capture("Drag-ProgressBar-PreviewPanel", {
      is_user_logged_in: true
    })
    if (!progressBarContainerRef.current || !generatedAudioDuration || duration <= 0) return;
    setIsDraggingAudio(true);
    dragStartXRef.current = e.clientX;
    dragStartAudioTimeRef.current = audioStartTime;
    if (generatedAudioRef.current) {
        generatedAudioRef.current.pause(); // Pause audio while dragging slider
    }
    e.preventDefault(); 
  };

  // Effect to handle mouse move and mouse up globally when dragging audio slider
  useEffect(() => {
    const handleAudioMouseMove = (e) => {
      if (!isDraggingAudio || !progressBarContainerRef.current || !generatedAudioDuration || duration <= 0) return;
      const progressBarRect = progressBarContainerRef.current.getBoundingClientRect();
      const pixelDiff = e.clientX - dragStartXRef.current;
      const timeDiff = (pixelDiff / progressBarRect.width) * duration;
      let newAudioStartTime = dragStartAudioTimeRef.current + timeDiff;
      const maxAudioStartTime = duration - generatedAudioDuration;
      newAudioStartTime = Math.max(0, Math.min(newAudioStartTime, maxAudioStartTime));
      setAudioStartTime(newAudioStartTime);
    };

    const handleAudioMouseUp = () => {
      if (isDraggingAudio) {
        setIsDraggingAudio(false);
        console.log("Audio drag ended. Latest start time:", audioStartTime); 

        // Only seek the audio element; let timeupdate handle play/pause sync
        const audioElement = generatedAudioRef.current;
        const videoElement = videoRef.current;
        if (canShowAudioSlider && audioElement && videoElement) {
            const videoTime = videoElement.currentTime;
            const audioShouldBeInRange = videoTime >= audioStartTime && videoTime < (audioStartTime + generatedAudioDuration);

            if (audioShouldBeInRange) {
                const audioTimeToSet = Math.max(0, videoTime - audioStartTime);
                if (isFinite(audioTimeToSet) && audioTimeToSet < generatedAudioDuration) {
                    console.log(`DragEnd: Seeking audio to ${audioTimeToSet} based on video time ${videoTime}`);
                    audioElement.currentTime = audioTimeToSet;
                    // Play command removed - timeupdate will handle this if video is playing
                }
            } 
            // No explicit pause needed here, timeupdate will handle it if necessary
        }
      }
    };

    if (isDraggingAudio) {
      document.addEventListener("mousemove", handleAudioMouseMove);
      document.addEventListener("mouseup", handleAudioMouseUp);
    } else {
      document.removeEventListener("mousemove", handleAudioMouseMove);
      document.removeEventListener("mouseup", handleAudioMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleAudioMouseMove);
      document.removeEventListener("mouseup", handleAudioMouseUp);
    };
  }, [isDraggingAudio, duration, generatedAudioDuration, audioStartTime]); // isPlaying is removed, audioStartTime added


  // --- Calculations for Slider --- 
  const canShowAudioSlider = generatedAudioDuration && generatedAudioDuration > 0 && duration && duration > 0;
  
  // Modified to ensure a minimum width of 20% for the audio slider
  const audioSliderWidthPercent = canShowAudioSlider && duration > 0 
      ? Math.max(20, (generatedAudioDuration / duration) * 100) // Minimum 20% width
      : 20; // Default minimum width even when loading
  
  const audioSliderLeftPercent = canShowAudioSlider && duration > 0 
      ? (audioStartTime / duration) * 100 
      : 0; // Default to 0 if duration is 0 or invalid

  const fullScreenClassName = `fixed inset-0 z-20 flex flex-col ${className || ''}`;

  // Create/revoke Blob URL when uploadedFile changes and stop loading indicator
  useEffect(() => {
    if (uploadedFile) {
      console.log("Creating Blob URL for uploaded video...");
      const url = URL.createObjectURL(uploadedFile);
      setAudioUrl(url);
      setIsLoading(false); // Stop loading now that the URL is ready
      console.log("Blob URL created, isLoading set to false.");
      return () => {
        console.log("Revoking Blob URL");
        URL.revokeObjectURL(url);
      };
    } else {
       // If uploadedFile becomes null (e.g., on remove), reset isLoading if it was true
       if (isLoading) {
         setIsLoading(false);
       }
       // Ensure audioUrl is null when no file is uploaded
       setAudioUrl(null);
    }
  }, [uploadedFile]); // Dependency is correct

  // Re-add the getVolumeIcon function definition
  const getVolumeIcon = () => {
    const currentVideoVolume = videoVolume;
    // Use w-4 h-4 for consistency with other icons
    if ((currentVideoVolume > 0) && (currentVideoVolume <= 30)) {
      return <VolumeOff className="w-4 h-4 text-white" />;
    }
    if (currentVideoVolume > 30 && currentVideoVolume < 80) {
      return <VolumeLow className="w-4 h-4 text-white" />;
    }
    if (currentVideoVolume === 0) {
      return <VolumeXmark className="w-4 h-4 text-white" />;
    }
    return <Volume className="w-4 h-4 text-white" />;
  };

  return (
    <div className={fullScreenClassName}>
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <Eye className="text-white" />
          <h2 className="text-lg font-normal">Preview</h2>
        </div>
        <button
          className="flex md:text-gray-400 text-white hover:text-white w-8 h-8 items-center justify-center md:bg-white/0 bg-white/5 md:hover:bg-white/5 rounded-full"
          onClick={() => {
            posthog.capture('Click-ClosePreviewPanel-PreviewPanel', {
              is_user_logged_in: true
            });
            onClose();
          }}
        >
          <Xmark className="w-4 h-4"/>
        </button>
      </div>
      {uploadedFile && (
        <div className="px-4 pt-0 py-4">
          <div className="flex justify-between items-end mb-1">
            <span className="text-sm text-gray-300">Music-Content Fit</span>
            <span className="text-sm font-medium ${fitScore ? (fitScore >= 70 ? 'text-green-400' : fitScore >= 40 ? 'text-amber-400' : 'text-red-400') : 'text-gray-400'}">
                {isAnalyzing ? (
                    'Analyzing...'
                ) : fitScore !== null ? (
                    `${fitScore}%`
                ) : isUploading ? (
                    'Uploading...'
                ) : (
                    'Waiting for video...' // Or keep static 80% until score comes?
                )}
            </span>
          </div>
          <div className="w-full bg-white/10 rounded-full h-1 overflow-hidden">
            {isAnalyzing || isUploading ? (
                 <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 animate-pulse w-full"></div>
            ) : (
                <div 
                className={`h-full rounded-full ${fitScore ? (fitScore >= 70 ? "bg-green-500" : fitScore >= 40 ? "bg-amber-500" : "bg-red-500") : "bg-gray-500"}`}
                style={{ width: `${fitScore !== null ? fitScore : 0}%` }} // Use fitScore for width, default 0 if null
                ></div>
            )}
          </div>
           {justification && !isAnalyzing && (
               <p className="text-xs text-gray-400 mt-1 font-extralight">{justification}</p>
           )}
           {analysisError && !isAnalyzing && (
               <p className="text-xs text-red-400 mt-1 font-extralight">Error: {analysisError}</p>
           )}
      </div>
      )}
      
      <div className="w-full border-t border-t-[#1d1b1d]"></div>
      {(isLoading || isUploading) ? (
        <div className="flex items-center justify-center flex-grow">
          <div className="flex flex-col items-center text-center justify-center border-dashed m-4 border-2 border-[#3c393c] w-full px-5 py-16 rounded-lg">
            <img className="w-10 h-10" src="/loadingAnimation.gif" alt="Loading" />
            <p className="text-white font-extralight mt-4">
                {isUploading ? 'Uploading video...' : 'Loading your content...'}
            </p>
          </div>
        </div>
      ) : uploadedFile && audioUrl ? (
        <div className="flex flex-col h-full flex-grow overflow-hidden">
          <div className="flex-1 px-4 py-4 flex flex-col justify-center items-center">
            <div className="relative w-full max-w-3xl max-h-60 rounded-lg bg-[#1D1B1D] flex items-center justify-center">
              <video
                ref={videoRef}
                src={audioUrl} 
                className="w-full h-full aspect-video object-contain rounded-md"
                preload="auto"
                muted={videoVolume === 0}
                onEnded={() => setIsPlaying(false)}
                onError={(e) => {
                  console.error("Video error:", e);
                  setToastMessage("Error playing video file");
                }}
              />
              <div className="absolute top-2 right-2 z-10">
                <button 
                  className="text-gray-200 p-1 rounded-full hover:bg-gray-700 bg-black bg-opacity-30 options-button"
                  onClick={handleOptionsClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="5" r="1"></circle>
                    <circle cx="12" cy="12" r="1"></circle>
                    <circle cx="12" cy="19" r="1"></circle>
                  </svg>
                </button>
                
                {showOptionsModal && (
                  <div className="absolute top-8 right-0 bg-[#100F10] rounded-lg shadow-lg py-1 px-1 w-32 options-modal z-20">
                    <button 
                      className="flex items-center px-2 py-2 rounded-md text-sm text-[#878188] hover:text-white hover:bg-white/5 w-full text-left"
                      onClick={handleReplace}
                    >
                      <ArrowRotateRight alt="Replace" className="w-4 h-4 mr-2"/>
                      Replace
                    </button>
                    <button 
                      className="flex items-center px-2 py-2 rounded-md text-sm text-[#878188] hover:text-white hover:bg-white/5 w-full text-left"
                      onClick={handleRemove}
                    >
                      <TrashBin className="w-4 h-4 mr-2" />
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col w-full mt-auto px-4 pb-4">
            <div className="w-full h-8 mb-2">
              <div 
                ref={progressBarContainerRef} 
                key={`audio-track-${duration}`}
                className="relative w-full h-full rounded-lg bg-[#1D1B1D] overflow-hidden border border-[#2C2A2C]"
              >
                {/* Placeholder audio slider when real data is loading */}
                {!canShowAudioSlider && generatedAudioSrc && (
                  <div
                      className="absolute top-1/2 -translate-y-1/2 h-7 p-[0.5px] border border-[#2C2A2C] rounded-md bg-gradient-to-r from-[#1D1B1D]/80 to-[#2C2A2C]/60 left-0 w-1/5"
                      style={{ zIndex: 4, minWidth: '80px' }}
                  >
                    <div className="w-full h-full px-[3px] flex items-center gap-1 rounded-[5px] bg-[#100F10]">
                      {generatedAudioCover ? (
                        <div className="w-5 h-5 rounded bg-[#1D1B1D] flex-shrink-0 overflow-hidden">
                          <img 
                            src={generatedAudioCover} 
                            alt=""
                            className="w-full h-full object-cover opacity-50"
                          />
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded bg-[#1D1B1D] flex-shrink-0"></div>
                      )}
                      <span className="flex-grow w-full h-2 bg-gradient-to-r from-[#2C2A2C] to-[#1D1B1D] rounded opacity-50"></span>
                      <div className="w-3 h-3 flex-shrink-0 text-[#2C2A2C]">
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="12" viewBox="0 0 13 12" fill="none" className="w-full h-full opacity-30">
                          <path fillRule="evenodd" clipRule="evenodd" d="M5.75 2.25C5.75 2.39774 5.7209 2.54403 5.66436 2.68052C5.60783 2.81701 5.52496 2.94103 5.4205 3.0455C5.31603 3.14996 5.19201 3.23283 5.05552 3.28936C4.91903 3.3459 4.77274 3.375 4.625 3.375C4.47726 3.375 4.33097 3.3459 4.19448 3.28936C4.05799 3.23283 3.93397 3.14996 3.8295 3.0455C3.72504 2.94103 3.64217 2.81701 3.58564 2.68052C3.5291 2.54403 3.5 2.39774 3.5 2.25C3.5 1.95163 3.61853 1.66548 3.8295 1.4545C4.04048 1.24353 4.32663 1.125 4.625 1.125C4.92337 1.125 5.20952 1.24353 5.4205 1.4545C5.63147 1.66548 5.75 1.95163 5.75 2.25ZM4.625 7.125C4.92337 7.125 5.20952 7.00647 5.4205 6.7955C5.63147 6.58452 5.75 6.29837 5.75 6C5.75 5.70163 5.63147 5.41548 5.4205 5.2045C5.20952 4.99353 4.92337 4.875 4.625 4.875C4.32663 4.875 4.04048 4.99353 3.8295 5.2045C3.61853 5.41548 3.5 5.70163 3.5 6C3.5 6.29837 3.61853 6.58452 3.8295 6.7955C4.04048 7.00647 4.32663 7.125 4.625 7.125ZM8.375 7.125C8.67337 7.125 8.95952 7.00647 9.1705 6.7955C9.38147 6.58452 9.5 6.29837 9.5 6C9.5 5.70163 9.38147 5.41548 9.1705 5.2045C8.95952 4.99353 8.67337 4.875 8.375 4.875C8.07663 4.875 7.79048 4.99353 7.5795 5.2045C7.36853 5.41548 7.25 5.70163 7.25 6C7.25 6.29837 7.36853 6.58452 7.5795 6.7955C7.79048 7.00647 8.07663 7.125 8.375 7.125ZM8.375 3.375C8.67337 3.375 8.95952 3.25647 9.1705 3.0455C9.38147 2.83452 9.5 2.54837 9.5 2.25C9.5 1.95163 9.38147 1.66548 9.1705 1.4545C8.95952 1.24353 8.67337 1.125 8.375 1.125C8.07663 1.125 7.79048 1.24353 7.5795 1.4545C7.36853 1.66548 7.25 1.95163 7.25 2.25C7.25 2.54837 7.36853 2.83452 7.5795 3.0455C7.79048 3.25647 8.07663 3.375 8.375 3.375ZM5.75 9.75C5.75 9.89774 5.7209 10.044 5.66436 10.1805C5.60783 10.317 5.52496 10.441 5.4205 10.5455C5.31603 10.65 5.19201 10.7328 5.05552 10.7894C4.91903 10.8459 4.77274 10.875 4.625 10.875C4.47726 10.875 4.33097 10.8459 4.19448 10.7894C4.05799 10.7328 3.93397 10.65 3.8295 10.5455C3.72504 10.441 3.64217 10.317 3.58564 10.1805C3.5291 10.044 3.5 9.89774 3.5 9.75C3.5 9.45163 3.61853 9.16548 3.8295 8.9545C4.04048 8.74353 4.32663 8.625 4.625 8.625C4.92337 8.625 5.20952 8.74353 5.4205 8.9545C5.63147 9.16548 5.75 9.45163 5.75 9.75ZM8.375 10.875C8.67337 10.875 8.95952 10.7565 9.1705 10.5455C9.38147 10.3345 9.5 10.0484 9.5 9.75C9.5 9.45163 9.38147 9.16548 9.1705 8.9545C8.95952 8.74353 8.67337 8.625 8.375 8.625C8.07663 8.625 7.79048 8.74353 7.5795 8.9545C7.36853 9.16548 7.25 9.45163 7.25 9.75C7.25 10.0484 7.36853 10.3345 7.5795 10.5455C7.79048 10.7565 8.07663 10.875 8.375 10.875Z" fill="currentColor"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* The actual audio slider */}
                {canShowAudioSlider && (
                  <div
                    ref={audioSliderRef}
                    className={`absolute top-1/2 -translate-y-1/2 h-7 p-[0.5px] border border-[#2C2A2C] hover:border-white/0 rounded-md group hover:bg-gradient-to-r from-[#FC10F2] to-[#3078E4] ${isDraggingAudio ? 'cursor-grabbing' : 'cursor-grab'} shadow-[0_0_12px_2px_rgba(252,16,242,0.2),_0_0_12px_2px_rgba(48,120,228,0.2)]`}
                    style={{
                        left: `${audioSliderLeftPercent}%`,
                        width: `${audioSliderWidthPercent}%`,
                        zIndex: 5, 
                        touchAction: 'none',
                        minWidth: '80px', // Add minimum width in pixels
                    }}
                    onMouseDown={handleAudioMouseDown}
                  >
                    <div className="w-full h-full px-[3px] flex items-center gap-1 rounded-[5px] bg-[#100F10]">
                      {generatedAudioCover && (
                        <img 
                            src={generatedAudioCover}
                            alt="Audio Cover"
                            className="w-5 h-5 rounded object-cover aspect-square flex-shrink-0"
                            draggable="false" 
                        />
                      )}
                      {generatedAudioName && (
                        <span className="flex-grow w-full overflow-hidden text-ellipsis whitespace-nowrap text-[#878188] group-hover:text-white text-[10px] font-medium">
                          {generatedAudioName}
                        </span>
                      )}
                      <div className="w-3 h-3 flex-shrink-0 text-white">
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="12" viewBox="0 0 13 12" fill="none" className="w-full h-full opacity-50 group-hover:opacity-100">
                          <path fillRule="evenodd" clipRule="evenodd" d="M5.75 2.25C5.75 2.39774 5.7209 2.54403 5.66436 2.68052C5.60783 2.81701 5.52496 2.94103 5.4205 3.0455C5.31603 3.14996 5.19201 3.23283 5.05552 3.28936C4.91903 3.3459 4.77274 3.375 4.625 3.375C4.47726 3.375 4.33097 3.3459 4.19448 3.28936C4.05799 3.23283 3.93397 3.14996 3.8295 3.0455C3.72504 2.94103 3.64217 2.81701 3.58564 2.68052C3.5291 2.54403 3.5 2.39774 3.5 2.25C3.5 1.95163 3.61853 1.66548 3.8295 1.4545C4.04048 1.24353 4.32663 1.125 4.625 1.125C4.92337 1.125 5.20952 1.24353 5.4205 1.4545C5.63147 1.66548 5.75 1.95163 5.75 2.25ZM4.625 7.125C4.92337 7.125 5.20952 7.00647 5.4205 6.7955C5.63147 6.58452 5.75 6.29837 5.75 6C5.75 5.70163 5.63147 5.41548 5.4205 5.2045C5.20952 4.99353 4.92337 4.875 4.625 4.875C4.32663 4.875 4.04048 4.99353 3.8295 5.2045C3.61853 5.41548 3.5 5.70163 3.5 6C3.5 6.29837 3.61853 6.58452 3.8295 6.7955C4.04048 7.00647 4.32663 7.125 4.625 7.125ZM8.375 7.125C8.67337 7.125 8.95952 7.00647 9.1705 6.7955C9.38147 6.58452 9.5 6.29837 9.5 6C9.5 5.70163 9.38147 5.41548 9.1705 5.2045C8.95952 4.99353 8.67337 4.875 8.375 4.875C8.07663 4.875 7.79048 4.99353 7.5795 5.2045C7.36853 5.41548 7.25 5.70163 7.25 6C7.25 6.29837 7.36853 6.58452 7.5795 6.7955C7.79048 7.00647 8.07663 7.125 8.375 7.125ZM8.375 3.375C8.67337 3.375 8.95952 3.25647 9.1705 3.0455C9.38147 2.83452 9.5 2.54837 9.5 2.25C9.5 1.95163 9.38147 1.66548 9.1705 1.4545C8.95952 1.24353 8.67337 1.125 8.375 1.125C8.07663 1.125 7.79048 1.24353 7.5795 1.4545C7.36853 1.66548 7.25 1.95163 7.25 2.25C7.25 2.54837 7.36853 2.83452 7.5795 3.0455C7.79048 3.25647 8.07663 3.375 8.375 3.375ZM5.75 9.75C5.75 9.89774 5.7209 10.044 5.66436 10.1805C5.60783 10.317 5.52496 10.441 5.4205 10.5455C5.31603 10.65 5.19201 10.7328 5.05552 10.7894C4.91903 10.8459 4.77274 10.875 4.625 10.875C4.47726 10.875 4.33097 10.8459 4.19448 10.7894C4.05799 10.7328 3.93397 10.65 3.8295 10.5455C3.72504 10.441 3.64217 10.317 3.58564 10.1805C3.5291 10.044 3.5 9.89774 3.5 9.75C3.5 9.45163 3.61853 9.16548 3.8295 8.9545C4.04048 8.74353 4.32663 8.625 4.625 8.625C4.92337 8.625 5.20952 8.74353 5.4205 8.9545C5.63147 9.16548 5.75 9.45163 5.75 9.75ZM8.375 10.875C8.67337 10.875 8.95952 10.7565 9.1705 10.5455C9.38147 10.3345 9.5 10.0484 9.5 9.75C9.5 9.45163 9.38147 9.16548 9.1705 8.9545C8.95952 8.74353 8.67337 8.625 8.375 8.625C8.07663 8.625 7.79048 8.74353 7.5795 8.9545C7.36853 9.16548 7.25 9.45163 7.25 9.75C7.25 10.0484 7.36853 10.3345 7.5795 10.5455C7.79048 10.7565 8.07663 10.875 8.375 10.875Z" fill="currentColor"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col pb-2 gap-2">
              <div 
                className="w-full h-1.5 bg-white/10 rounded-full cursor-pointer"
                onClick={handleSeek}
                onMouseEnter={() => setIsProgressHovered(true)}
                onMouseLeave={() => setIsProgressHovered(false)}
              >
                <div 
                  className={`h-full rounded-full relative ${ 
                    isProgressHovered 
                      ? 'bg-gradient-to-r from-[#FC10F2] to-[#3078E4]' 
                      : 'bg-white' 
                  }`}
                  style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                >
                  {isProgressHovered && (
                    <div className="absolute right-0 top-1/2 w-4 h-4 bg-white rounded-full transform translate-x-1/2 -translate-y-1/2 shadow-lg z-10"></div>
                  )}
                </div>
              </div>
              <div className="flex justify-between text-xs text-white/40">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
            
            <div className="flex justify-center items-center space-x-5 mb-4">
              <button 
                className="text-white flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/10"
                onClick={handleRewind}
              >
                <img 
                  src="/preview-panel-icons/10-seconds-reverse.svg" 
                  alt="Rewind 10 seconds"
                  width="16"
                  height="16"
                  className="text-white"
                />
              </button>
              <button 
                className={`relative bg-white text-black w-11 h-11 rounded-full ${!isPlaying && generatedAudioSrc && !isAudioLoaded ? 'opacity-70' : 'hover:bg-gray-200'} flex items-center justify-center`}
                onClick={handlePlayPause}
                disabled={!isPlaying && generatedAudioSrc && !isAudioLoaded}
              >
                {isPlaying ? (
                  <PauseFill className="text-black" width={18} height={18} />
                ) : generatedAudioSrc && !isAudioLoaded ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <TriangleRightFill className="text-black translate-x-[1px]" width={18} height={18} />
                )}
              </button>
              <button 
                className="text-white flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/10"
                onClick={handleForward}
              >
                <img 
                  src="/preview-panel-icons/10-seconds-forward.svg" 
                  alt="Forward 10 seconds"
                  width="16"
                  height="16"
                  className="text-white"
                />
              </button>
            </div>
          </div>
          <div className="w-full border-t border-t-[#1d1b1d]"></div>
          <div className="grid grid-cols-2 gap-4 px-4 py-4">
            <div>
              <div className="text-sm text-gray-300 mb-2">Video volume</div>
              <div className="flex items-center">
                <button 
                  className={`text-white mr-2 focus:outline-none ${videoVolume === 0 ? 'opacity-50' : ''}`}
                  onClick={toggleVideoMute}
                >
                  {getVolumeIcon()}
                </button>
                <div 
                  ref={videoVolumeRef}
                  className="relative w-full h-10 flex items-center cursor-pointer"
                  onMouseDown={() => {
                    setIsDraggingVideoVolume(true)
                    posthog.capture("Drag-VideoVolumeBar-PreviewPanel", {
                      is_user_logged_in: true
                    })
                  }}
                  onClick={handleVideoVolumeChange}
                >
                  <div className="h-1.5 bg-white/10 rounded-full w-full relative">
                    <div 
                      className="absolute top-0 left-0 h-full bg-white rounded-full"
                      style={{ width: `${videoVolume}%` }}
                    ></div>
                    <div 
                      className="absolute top-1/2 w-3 h-3 bg-white rounded-full transform -translate-y-1/2 shadow -ml-1.5" 
                      style={{ left: `${videoVolume}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="text-sm text-gray-300 mb-2">Music volume</div>
              <div className="flex items-center">
                <button 
                  className={`text-white mr-2 focus:outline-none ${mediaVolume === 0 ? 'opacity-50' : ''}`}
                  onClick={toggleMediaMute}
                >
                  {mediaVolume === 0 ? (
                    <VolumeXmark className="w-4 h-4" />
                  ) : (
                    mediaVolume <= 30 ? (
                      <VolumeOff className="w-4 h-4" />
                    ) : (
                      mediaVolume <= 80 ? (
                        <VolumeLow className="w-4 h-4" />
                      ) : (
                        <Volume className="w-4 h-4" />
                      )
                    )
                  )}
                </button>
                <div 
                  ref={mediaVolumeRef}
                  className="relative w-full h-10 flex items-center cursor-pointer"
                  onMouseDown={() => {
                    setIsDraggingMediaVolume(true)
                    posthog.capture("Drag-MusicVolumeBar-PreviewPanel", {
                      is_user_logged_in: true
                    })
                  }}
                  onClick={handleMediaVolumeChange}
                >
                  <div className="h-1.5 bg-white/10 rounded-full w-full relative">
                    <div 
                      className="absolute top-0 left-0 h-full bg-white rounded-full"
                      style={{ width: `${mediaVolume}%` }}
                    ></div>
                    <div 
                      className="absolute top-1/2 w-3 h-3 bg-white rounded-full transform -translate-y-1/2 shadow -ml-1.5" 
                      style={{ left: `${mediaVolume}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* --- Hidden Audio Player for Generated Music --- */} 
           {generatedAudioSrc && (
                <audio 
                    ref={generatedAudioRef}
                    src={generatedAudioSrc}
                    preload="auto"
                    style={{ display: 'none' }}
                    onLoadStart={() => {
                        console.log("Audio load started");
                        setIsAudioLoaded(false);
                    }}
                    onError={(e) => {
                        console.error("Generated audio error:", e);
                        setToastMessage("Error loading generated audio");
                        setIsAudioLoaded(false);
                    }}
                    onCanPlay={() => {
                        console.log("Generated audio can play.");
                        setIsAudioLoaded(true);
                    }}
                    // Add onLoadedData event handler
                    onLoadedData={() => {
                        console.log("Audio data loaded successfully.");
                        setIsAudioLoaded(true);
                        // If audio loaded after video is already playing, and should be playing, start it
                        if (isPlaying && videoRef.current && !videoRef.current.paused) {
                            const videoTime = videoRef.current.currentTime;
                            const audioShouldPlay = videoTime >= audioStartTime && 
                              videoTime < (audioStartTime + generatedAudioDuration);
                            
                            if (audioShouldPlay) {
                                const audioTimeToSet = Math.max(0, videoTime - audioStartTime);
                                generatedAudioRef.current.currentTime = audioTimeToSet;
                                generatedAudioRef.current.play().catch(e => 
                                  console.error("Failed to play audio after load:", e), Sentry.captureException(e));
                            }
                        }
                    }}
                />
            )} 
        </div>
      ) : (
        <div className="flex items-center justify-center flex-grow">
          <div
            ref={dropRef}
            className="border-dashed m-4 border-2 border-[#3c393c] w-full px-5 py-16 rounded-md text-center"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <ArrowShapeUpFromLine className="w-9 h-9 text-white mx-auto mb-3" />
            <p className="mb-1 text-base font-extralight">
            Drop your content here or{" "}
              <label
                htmlFor="fileInput"
                className="text-white cursor-pointer underline"
              >
                Browse
              </label>
            </p>
            <p className="text-white/60 font-extralight text-sm">
              File supported: Video
            </p>
            <p className="text-white/60 font-extralight text-sm">File size: max 20 MB</p>
          </div>
        </div>
      )}
      
      <input
        type="file"
        accept="video/*"
        onChange={(e) => {
          console.log("File input change detected", e.target.files?.length > 0 ? e.target.files[0].name : "No files");
          if (e.target.files && e.target.files[0]) {
            handleFileChange(e);
          }
        }}
        className="hidden"
        id="fileInput"
        ref={fileInputRef}
        key="file-input" // Add key to help with React reconciliation
        onClick={(e)=>posthog.capture("Drag-MusicLayer-PreviewPanel", {
          is_user_logged_in: true
        })}
      />
      
      {toastMessage && (
        <Toast
          type={toastType}
          message={toastMessage}
          onClose={() => setToastMessage("")}
        />
      )}
    </div>
  );
};

export default PreviewPanel;